<template>
  <div class="sd" :class="{'sd-dark': dark, 'sd-static': staticMode}" :dir="isAr ? 'rtl' : 'ltr'" :lang="lang">
    <!-- header -->
    <header v-if="!hideHeader" class="sd-head">
      <div class="sd-wrap">
        <div class="sd-crest">
          <svg viewBox="0 0 24 24" fill="none"><path d="M12 21v-8" stroke="#d9c48a" stroke-width="1.6" stroke-linecap="round" /><path d="M12 13c0-3 2.2-5.4 5-6-1 2.8-2.6 4.6-5 6zm0 0c0-3-2.2-5.4-5-6 1 2.8 2.6 4.6 5 6zm0-2.5c.4-2.6 2-4.4 4-5.5-2.6.2-4 2-4 2s-1.4-1.8-4-2c2 1.1 3.6 2.9 4 5.5z" fill="#e5d5a4" /><path d="M5 21h14" stroke="#d9c48a" stroke-width="1.6" stroke-linecap="round" /></svg>
        </div>
        <div><h1>{{ t('title') }}</h1><p class="sub">{{ t('subtitle') }}</p></div>
        <div class="grow" />
        <div class="sd-chip"><span class="dot" /><span><b>{{ t('updated') }}</b><span class="tnum">{{ D.generatedAt || '—' }}</span></span></div>
        <div class="sd-chip"><span><b>{{ t('data_scope') }}</b><span class="tnum num-ltr">{{ coverage }}</span></span></div>
        <v-menu offset-y>
          <template #activator="{ on, attrs }"><v-btn small depressed v-bind="attrs" v-on="on">{{ t('exp_btn') }}</v-btn></template>
          <v-list dense>
            <v-list-item @click="print"><v-list-item-icon><v-icon small>mdi-printer</v-icon></v-list-item-icon><v-list-item-title>{{ t('exp_pdf') }}</v-list-item-title></v-list-item>
            <v-list-item @click="exportCsvRegions(D)"><v-list-item-icon><v-icon small>mdi-download</v-icon></v-list-item-icon><v-list-item-title>{{ t('exp_csv_r') }}</v-list-item-title></v-list-item>
            <v-list-item @click="exportCsvSurveys(D)"><v-list-item-icon><v-icon small>mdi-download</v-icon></v-list-item-icon><v-list-item-title>{{ t('exp_csv_s') }}</v-list-item-title></v-list-item>
            <v-list-item @click="exportJson(D)"><v-list-item-icon><v-icon small>mdi-code-json</v-icon></v-list-item-icon><v-list-item-title>{{ t('exp_json') }}</v-list-item-title></v-list-item>
          </v-list>
        </v-menu>
        <v-btn small depressed class="sd-refresh-btn" :loading="store.state.refreshing" :disabled="!store.state.data" @click="refresh">
          <v-icon small left>mdi-refresh</v-icon>{{ t('refresh_btn') }}
          <template #loader><v-progress-circular indeterminate size="14" width="2" class="me-2" />{{ t('refreshing') }}</template>
        </v-btn>
        <!-- the dashboard renders full page (no platform header), so this is the way back -->
        <v-btn small depressed class="sd-home-btn" :to="{ name: 'landing' }" exact>
          <v-icon small left>mdi-home-outline</v-icon>{{ t('go_home') }}
        </v-btn>
      </div>
      <div class="sd-nav">
        <v-tabs :value="tabIndex" background-color="transparent" hide-slider show-arrows @change="onTab">
          <v-tab v-for="tb in visibleTabs" :key="tb.name">{{ t(tb.label) }}</v-tab>
        </v-tabs>
      </div>
    </header>

    <!-- body -->
    <div class="sd-wrap">
      <div v-if="store.state.loading && !store.state.data" class="dload"><v-progress-circular indeterminate color="primary" size="26" width="3" style="margin-inline-end:12px" />{{ t('loading') }}</div>
      <div v-else-if="store.state.error" class="sd-card sd-warnbox" style="display:flex;gap:14px;align-items:center;flex-wrap:wrap">⚠️ {{ t('load_err') }} <code style="font-size:12px;opacity:.8">{{ store.state.error.message }}</code><span class="grow" /><v-btn small depressed color="primary" @click="store.load()">{{ t('retry') }}</v-btn></div>
      <!-- keyed on the store version so every view re-mounts and refetches after a refresh -->
      <template v-else-if="store.state.data">
        <div :key="store.state.version">
          <sd-executive-view v-if="view.name === 'exec'" @navigate="navigate" />
          <sd-indicators-view v-else-if="view.name === 'indicators'" :section="view.hash" :initial-year="view.year" :initial-metric="view.metric || 'part'" @navigate="navigate" />
          <sd-surveys-view v-else-if="view.name === 'surveys'" @navigate="navigate" />
          <sd-survey-detail-view v-else-if="view.name === 'survey'" :id="view.id" @navigate="navigate" />
          <sd-strategic-view v-else-if="view.name === 'strategic'" @navigate="navigate" />
          <sd-compare-view v-else-if="view.name === 'compare'" :query="view.query || {}" @update:query="onCompareQuery" @navigate="navigate" />
        </div>
        <footer><div>{{ t('foot') }} · {{ t('geo_credit') }}</div><div class="sig">{{ t('sig') }}</div></footer>
      </template>
    </div>

    <!-- shared tooltip -->
    <div v-if="tip" class="sd-tip" :style="tipStyle" v-html="tip.html" />
  </div>
</template>
<script>
/**
 * DashboardShell — hosts the six dashboard views (executive, indicators, surveys, survey detail,
 * strategic, comparisons) under the app's own header. Language follows the app; the view is
 * kept in the route so deep links and the back button work.
 *
 * Props
 *   api          { getSnapshot(), getSurvey(id), getStrategic() } — see services/DashboardService.js
 *   lang         'ar' | 'en' (the app's current language)
 *   hideHeader   drop the dashboard header + tabs
 *   staticMode   disables animations (screenshots / print)
 */
import Vue from 'vue'
import './styles/dashboard.css'
import { createStore } from './store.js'
import { coverage } from './lib/stats.js'
import { exportCsvRegions, exportCsvSurveys, exportJson } from './lib/exportCsv.js'
import { viewToRoute, routeToView } from './nav.js'
import { tipDirective } from './directives/tip.js'
import SdExecutiveView from './views/ExecutiveView.vue'
import SdIndicatorsView from './views/IndicatorsView.vue'
import SdSurveysView from './views/SurveysView.vue'
import SdSurveyDetailView from './views/SurveyDetailView.vue'
import SdStrategicView from './views/StrategicView.vue'
import SdCompareView from './views/CompareView.vue'

// every view and chart uses v-tip, so it is registered once for the whole subtree
Vue.directive('tip', tipDirective)

const TABS = [
  { name: 'exec', label: 'nav_home' }, { name: 'indicators', label: 'nav_kpi' }, { name: 'surveys', label: 'nav_srv' },
  { name: 'strategic', label: 'nav_str' }, { name: 'compare', label: 'nav_cmp' },
]
export default {
  name: 'DashboardShell',
  components: { SdExecutiveView, SdIndicatorsView, SdSurveysView, SdSurveyDetailView, SdStrategicView, SdCompareView },
  provide() { return { sdStore: this.store } },
  props: {
    api: { type: Object, required: true },
    lang: { type: String, default: 'ar' },
    hideHeader: { type: Boolean, default: false },
    staticMode: { type: Boolean, default: false },
  },
  data() {
    const store = createStore(this.api, { lang: this.lang === 'en' ? 'en' : 'ar', dark: false, staticMode: this.staticMode })
    return { store }
  },
  computed: {
    D() { return this.store.state.data || {} },
    isAr() { return this.store.state.lang === 'ar' },
    dark() { return this.store.state.dark },
    tip() { return this.store.state.tip },
    tipStyle() { const t = this.tip, pad = 14, w = 240; let x = t.x + pad; if (x + w > window.innerWidth - 8) x = t.x - w - pad; return { left: x + 'px', top: (t.y + pad) + 'px' } },
    coverage() { return coverage(this.D) },
    // the strategic track has no database source yet; its tab only shows when the API returns it
    visibleTabs() { return TABS.filter((tb) => tb.name !== 'strategic' || this.store.state.strategic) },
    view() { return routeToView(this.$route) },
    tabIndex() { const n = this.view.name === 'survey' ? 'surveys' : this.view.name; const i = this.visibleTabs.findIndex((t) => t.name === n); return i < 0 ? 0 : i },
  },
  watch: {
    lang(l) { this.store.setLang(l === 'en' ? 'en' : 'ar') },
  },
  created() {
    this.store.load()
  },
  methods: {
    t(k, v) { return this.store.t(k, v) },
    exportCsvRegions, exportCsvSurveys, exportJson,
    print() { window.print() },
    async refresh() {
      try {
        await this.store.refresh()
        this.$root.$notification(this.t('refresh_ok'), 'success')
      } catch (e) {
        this.$root.$notification(this.t('refresh_err'), 'error')
      }
    },
    onTab(i) { const tb = this.visibleTabs[i]; if (tb && tb.name !== this.view.name) this.navigate({ name: tb.name }) },
    navigate(v) {
      const r = viewToRoute(v)
      if (this.$route.fullPath !== this.$router.resolve(r).route.fullPath) this.$router.push(r).catch(() => {})
      if (!v.hash) window.scrollTo({ top: 0, behavior: 'auto' })
    },
    onCompareQuery(q) {
      this.$router.replace({ name: 'dashboard-compare', query: q }).catch(() => {})
    },
  },
}
</script>
